import { APICONFIG } from '@config/api.config';
import { getDatabase } from '@rxdb/config/databaseConfig';
import { IQueryResponse } from '@services/api';
import { APIService } from '@services/api/common-services';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ISiteListResponse, ISiteDetailResponse } from '.';
import { usePermissionCheckForApiCallback } from '../../../../authGuard';

export const useSiteListQuery = (): IQueryResponse<Array<ISiteListResponse>> => {
	const [shouldFetch, setShouldFetch] = useState(true);
	const [isDbChecked, setIsDbChecked] = useState(false);
	const { permissionForApi } = usePermissionCheckForApiCallback({ componentName: 'AllSites' });

	const fetchSitesData = async (): Promise<Array<ISiteListResponse>> => {
		const response = await APIService.getData<Array<ISiteListResponse>>(`${APICONFIG.LMS_SITE_ADD_OR_LIST_API}`, {
			limit: '0',
		});
		if (response) {
			const db = await getDatabase();
			const allSites = await db.lms_site_list.find().exec();
			const primaryId = allSites.map((doc: { primary: string }) => doc.primary);
			await db.lms_site_list.bulkRemove(primaryId);
			await db.lms_site_list.bulkInsert(response);
		}
		return response;
	};

	const checkDb = async (): Promise<boolean> => {
		const db = await getDatabase();
		const result = await db.lms_site_list.find().exec();
		return result.length > 0;
	};

	useEffect(() => {
		const checkDatabase = async (): Promise<void> => {
			const exists = await checkDb();
			setShouldFetch(!exists);
			setIsDbChecked(true);
		};
		checkDatabase();
	}, []);

	const { data, error, isLoading, isPending, refetch, isFetching } = useQuery({
		queryKey: ['SitesList'],
		queryFn: fetchSitesData,
		enabled: permissionForApi ? isDbChecked && shouldFetch : false,
		refetchOnWindowFocus: false,
		retry: false,
	});

	return {
		data,
		error,
		isLoading,
		isPending,
		refetch,
		isFetching,
	};
};

export const useLmsContractorSiteSummaryQuery = (
	contractorId?: number | null,
	membershipno_c?: string | null
): IQueryResponse<ISiteDetailResponse> => {
	const fetchContractorSiteSummary = async (): Promise<ISiteDetailResponse> => {
		const url = `${APICONFIG.LMS_CONTRACTOR_SITE_SUMMARY}${contractorId}/contractor_site_summary`;
		return await APIService.getData<ISiteDetailResponse>(url, {
			limit: '0',
			membership_no: membershipno_c || '',
		});
	};

	const { data, error, isLoading, isPending, isFetching } = useQuery({
		queryKey: ['lmsContractorSiteSummary', contractorId],
		queryFn: fetchContractorSiteSummary,
		enabled: !!contractorId,
		refetchOnWindowFocus: false,
	});

	return {
		data,
		error,
		isLoading,
		isPending,
		isFetching,
	};
};
